import { useEffect, useMemo, useState } from 'react';
import { usePublicationMonitors, type PublicationMonitor, type PublicationMonitorType } from '@/hooks/usePublicationMonitors';

type PublicationMonitorsPanelProps = {
  onRefreshInbox: () => Promise<void> | void;
};

type MonitorFormState = {
  name: string;
  monitorType: PublicationMonitorType;
  processNumber: string;
  lawyerOab: string;
  lawyerUf: string;
  lawyerName: string;
  tribunal: string;
  caderno: string;
  defaultStartDate: string;
  defaultEndDate: string;
  lookbackDays: string;
  isActive: boolean;
};

const emptyForm: MonitorFormState = {
  name: '',
  monitorType: 'mixed',
  processNumber: '',
  lawyerOab: '',
  lawyerUf: '',
  lawyerName: '',
  tribunal: '',
  caderno: '',
  defaultStartDate: '',
  defaultEndDate: '',
  lookbackDays: '30',
  isActive: true,
};

const monitorTypeOptions: Array<{ value: PublicationMonitorType; label: string }> = [
  { value: 'mixed', label: 'Misturado' },
  { value: 'process', label: 'Processo' },
  { value: 'lawyer_oab', label: 'OAB' },
  { value: 'lawyer_name', label: 'Advogado' },
];

const toFormState = (monitor: PublicationMonitor): MonitorFormState => ({
  name: monitor.name,
  monitorType: monitor.monitorType,
  processNumber: monitor.processNumber ?? '',
  lawyerOab: monitor.lawyerOab ?? '',
  lawyerUf: monitor.lawyerUf ?? '',
  lawyerName: monitor.lawyerName ?? '',
  tribunal: monitor.tribunal ?? '',
  caderno: monitor.caderno ?? '',
  defaultStartDate: monitor.defaultStartDate ?? '',
  defaultEndDate: monitor.defaultEndDate ?? '',
  lookbackDays: String(monitor.lookbackDays ?? 30),
  isActive: monitor.isActive,
});

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(date);
};

export default function PublicationMonitorsPanel({ onRefreshInbox }: PublicationMonitorsPanelProps) {
  const {
    monitors,
    runs,
    cronStatus,
    isLoadingMonitors,
    isLoadingRuns,
    isLoadingCronStatus,
    isSavingMonitor,
    monitorError,
    monitorSuccess,
    runError,
    runSuccess,
    loadRuns,
    loadCronStatus,
    createMonitor,
    updateMonitor,
    deleteMonitor,
    runMonitor,
    clearPublicationMonitorFeedback,
  } = usePublicationMonitors();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<MonitorFormState>(emptyForm);
  const [selectedMonitorId, setSelectedMonitorId] = useState<string>('');

  useEffect(() => {
    if (!selectedMonitorId && monitors.length > 0) {
      setSelectedMonitorId(monitors[0].id);
    }
  }, [monitors, selectedMonitorId]);

  useEffect(() => {
    if (selectedMonitorId) {
      void loadRuns(selectedMonitorId);
    }
  }, [loadRuns, selectedMonitorId]);

  const selectedMonitor = useMemo(
    () => monitors.find((monitor) => monitor.id === selectedMonitorId) ?? monitors[0] ?? null,
    [monitors, selectedMonitorId]
  );

  const recentRuns = useMemo(() => {
    if (selectedMonitor) {
      return runs.filter((run) => run.monitorId === selectedMonitor.id).slice(0, 5);
    }

    return runs.slice(0, 5);
  }, [runs, selectedMonitor]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    clearPublicationMonitorFeedback();
  };

  const openEdit = (monitor: PublicationMonitor) => {
    setEditingId(monitor.id);
    setForm(toFormState(monitor));
    setSelectedMonitorId(monitor.id);
    clearPublicationMonitorFeedback();
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const submitForm = async () => {
    if (!form.name.trim()) {
      return;
    }

    const payload = {
      name: form.name.trim(),
      monitorType: form.monitorType,
      processNumber: form.processNumber.trim() || null,
      lawyerOab: form.lawyerOab.trim() || null,
      lawyerUf: form.lawyerUf.trim().toUpperCase() || null,
      lawyerName: form.lawyerName.trim() || null,
      tribunal: form.tribunal.trim() || null,
      caderno: form.caderno.trim() || null,
      defaultStartDate: form.defaultStartDate || null,
      defaultEndDate: form.defaultEndDate || null,
      lookbackDays: form.lookbackDays.trim() || null,
      isActive: form.isActive,
    };

    if (editingId) {
      await updateMonitor(editingId, payload);
      await loadRuns(editingId);
    } else {
      const created = await createMonitor(payload);
      setSelectedMonitorId(created.id);
      await loadRuns(created.id);
    }

    resetForm();
  };

  const handleRun = async (monitor: PublicationMonitor) => {
    const result = await runMonitor(monitor.id);
    if ((result.totalInserted ?? 0) > 0) {
      await onRefreshInbox();
    }
    await loadRuns(monitor.id);
  };

  const lastRunByMonitor = useMemo(() => {
    const map = new Map<string, (typeof runs)[number]>();
    for (const run of runs) {
      if (run.monitorId && !map.has(run.monitorId)) {
        map.set(run.monitorId, run);
      }
    }
    return map;
  }, [runs]);

  return (
    <details className="rounded-2xl border border-dashed border-primary-200 bg-primary-50/40 p-4">
      <summary className="cursor-pointer list-none text-sm font-semibold text-primary-800">Monitores DJEN/CNJ</summary>

      <div className="mt-4 space-y-4">
        <p className="text-xs text-foreground-500">
          Monitores ativos são executados automaticamente pelo scheduler. Use Executar para realizar uma consulta imediata adicional.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={openCreate}
            className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            Novo monitor
          </button>
          <button
            type="button"
            onClick={() => void loadRuns(selectedMonitorId || null)}
            className="rounded-lg border border-background-200 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
          >
            Atualizar histórico
          </button>
          <button
            type="button"
            onClick={() => void loadCronStatus()}
            className="rounded-lg border border-background-200 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
          >
            Atualizar automação
          </button>
        </div>

        {(monitorError || monitorSuccess || runError || runSuccess) && (
          <div className="space-y-2">
            {monitorError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{monitorError}</div>}
            {monitorSuccess && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{monitorSuccess}</div>}
            {runError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{runError}</div>}
            {runSuccess && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{runSuccess}</div>}
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="xl:col-span-2 rounded-2xl border border-dashed border-primary-200 bg-primary-50/40 p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-base font-semibold text-primary-900">Automação DJEN/CNJ</h3>
              {isLoadingCronStatus && <span className="text-xs text-primary-700">Carregando...</span>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="rounded-xl border border-background-200 bg-white p-3">
                <p className="text-[10px] uppercase tracking-wider text-foreground-400">Monitores ativos</p>
                <p className="mt-1 text-lg font-bold text-foreground-900">{cronStatus?.activeMonitorCount ?? 0}</p>
              </div>
              <div className="rounded-xl border border-background-200 bg-white p-3">
                <p className="text-[10px] uppercase tracking-wider text-foreground-400">Última execução automática</p>
                <p className="mt-1 text-sm font-semibold text-foreground-900">{formatDateTime(cronStatus?.lastAutomaticRunAt ?? cronStatus?.lastAutomaticRun?.finishedAt ?? cronStatus?.lastAutomaticRun?.startedAt)}</p>
              </div>
              <div className="rounded-xl border border-background-200 bg-white p-3">
                <p className="text-[10px] uppercase tracking-wider text-foreground-400">Status</p>
                <p className="mt-1 text-sm font-semibold text-foreground-900">{cronStatus?.lastAutomaticStatus ?? '-'}</p>
              </div>
              <div className="rounded-xl border border-background-200 bg-white p-3">
                <p className="text-[10px] uppercase tracking-wider text-foreground-400">Último erro</p>
                <p className="mt-1 text-sm font-semibold text-foreground-900">{cronStatus?.lastAutomaticError ?? '-'}</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-background-200 bg-white p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-base font-semibold text-foreground-900">{editingId ? 'Editar monitor' : 'Cadastro de monitor'}</h3>
              {(isLoadingMonitors || isSavingMonitor) && <span className="text-xs text-foreground-500">{isLoadingMonitors ? 'Carregando...' : 'Salvando...'}</span>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="space-y-1 md:col-span-2">
                <span className="text-xs font-medium text-foreground-600">Nome do monitor</span>
                <input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" placeholder="Monitor TJSP - Fraude Pix" />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Tipo</span>
                <select value={form.monitorType} onChange={(event) => setForm((current) => ({ ...current, monitorType: event.target.value as PublicationMonitorType }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm">
                  {monitorTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Status</span>
                <select value={String(form.isActive)} onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.value === 'true' }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm">
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </select>
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Processo</span>
                <input value={form.processNumber} onChange={(event) => setForm((current) => ({ ...current, processNumber: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">OAB</span>
                <input value={form.lawyerOab} onChange={(event) => setForm((current) => ({ ...current, lawyerOab: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">
                  UF da OAB
                </span>
                <input
                  value={form.lawyerUf}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      lawyerUf: event.target.value.toUpperCase().slice(0, 2),
                    }))
                  }
                  maxLength={2}
                  className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm"
                  placeholder="MG"
                />
                {form.lawyerOab.trim() && !form.lawyerUf.trim() && (
                  <span className="block text-[11px] text-amber-700">
                    Informe a UF quando utilizar número da OAB.
                  </span>
                )}
              </label>
              <label className="space-y-1 md:col-span-2">
                <span className="text-xs font-medium text-foreground-600">Nome do advogado</span>
                <input value={form.lawyerName} onChange={(event) => setForm((current) => ({ ...current, lawyerName: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Tribunal</span>
                <input value={form.tribunal} onChange={(event) => setForm((current) => ({ ...current, tribunal: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Caderno</span>
                <input value={form.caderno} onChange={(event) => setForm((current) => ({ ...current, caderno: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Data inicial</span>
                <input type="date" value={form.defaultStartDate} onChange={(event) => setForm((current) => ({ ...current, defaultStartDate: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-foreground-600">Data final</span>
                <input type="date" value={form.defaultEndDate} onChange={(event) => setForm((current) => ({ ...current, defaultEndDate: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" />
              </label>
              <label className="space-y-1 md:col-span-2">
                <span className="text-xs font-medium text-foreground-600">Lookback em dias</span>
                <input type="number" min="1" value={form.lookbackDays} onChange={(event) => setForm((current) => ({ ...current, lookbackDays: event.target.value }))} className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm" />
              </label>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => void submitForm()} disabled={
                  isSavingMonitor ||
                  !form.name.trim() ||
                  Boolean(form.lawyerOab.trim() && !form.lawyerUf.trim())
                } className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60">
                {editingId ? 'Salvar monitor' : 'Cadastrar monitor'}
              </button>
              <button type="button" onClick={resetForm} className="rounded-lg border border-background-200 px-4 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100">
                Limpar
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-background-200 bg-white p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-base font-semibold text-foreground-900">Monitores cadastrados</h3>
              <span className="text-xs text-foreground-500">{monitors.length} monitor(es)</span>
            </div>

            <div className="space-y-2 max-h-[32rem] overflow-auto pr-1">
              {monitors.length === 0 ? (
                <div className="rounded-xl border border-dashed border-background-200 bg-background-50 px-4 py-6 text-sm text-foreground-500">
                  Nenhum monitor cadastrado ainda.
                </div>
              ) : (
                monitors.map((monitor) => {
                  const lastRun = lastRunByMonitor.get(monitor.id) ?? null;
                  const active = monitor.isActive;

                  return (
                    <article key={monitor.id} className="rounded-xl border border-background-200 bg-background-50 p-3 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <button type="button" onClick={() => setSelectedMonitorId(monitor.id)} className="text-left text-sm font-semibold text-foreground-900 hover:text-primary-600">
                            {monitor.name}
                          </button>
                          <p className="text-xs text-foreground-500">
                            {monitor.lawyerOab
                              ? `OAB · ${monitor.lawyerOab}${monitor.lawyerUf ? `/${monitor.lawyerUf}` : ''}`
                              : `${monitor.monitorType} · ${monitor.processNumber || monitor.lawyerName || 'Monitor flexível'}`}
                          </p>
                        </div>
                        <span className={`rounded-full px-2 py-1 text-[11px] font-medium ${active ? 'bg-emerald-50 text-emerald-700' : 'bg-background-200 text-foreground-500'}`}>
                          {active ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs text-foreground-600">
                        <div className="rounded-lg border border-background-200 bg-white p-2">Última execução: {formatDateTime(lastRun?.startedAt ?? monitor.lastRunAt)}</div>
                        <div className="rounded-lg border border-background-200 bg-white p-2">Último sucesso: {formatDateTime(monitor.lastSuccessAt)}</div>
                        <div className="rounded-lg border border-background-200 bg-white p-2 col-span-2">Último erro: {monitor.lastError || lastRun?.errors?.[0] || '-'}</div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => void handleRun(monitor)} disabled={isSavingMonitor || !active} className="rounded-lg bg-primary-500 px-3 py-2 text-xs font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60">
                          Executar
                        </button>
                        <button type="button" onClick={() => openEdit(monitor)} className="rounded-lg border border-background-200 px-3 py-2 text-xs font-medium text-foreground-700 hover:bg-background-100">
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => void updateMonitor(monitor.id, { isActive: !monitor.isActive })}
                          className="rounded-lg border border-background-200 px-3 py-2 text-xs font-medium text-foreground-700 hover:bg-background-100"
                        >
                          {active ? 'Desativar' : 'Ativar'}
                        </button>
                        <button type="button" onClick={() => void deleteMonitor(monitor.id)} className="rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50">
                          Excluir
                        </button>
                      </div>
                    </article>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-background-200 bg-white p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base font-semibold text-foreground-900">Histórico de execuções</h3>
            {selectedMonitor && <span className="text-xs text-foreground-500">Monitor: {selectedMonitor.name}</span>}
          </div>

          {isLoadingRuns ? (
            <div className="rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-600">Carregando histórico...</div>
          ) : recentRuns.length === 0 ? (
            <div className="rounded-xl border border-dashed border-background-200 bg-background-50 px-4 py-6 text-sm text-foreground-500">
              Nenhuma execução registrada ainda.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-foreground-400">
                  <tr>
                    <th className="py-2 pr-4">Data</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Consultado</th>
                    <th className="py-2 pr-4">Inserido</th>
                    <th className="py-2 pr-4">Duplicadas</th>
                    <th className="py-2 pr-4">Erros</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200">
                  {recentRuns.map((run) => (
                    <tr key={run.id}>
                      <td className="py-2 pr-4 text-foreground-700">{formatDateTime(run.startedAt)}</td>
                      <td className="py-2 pr-4 text-foreground-700">
  {run.status === 'success' && run.totalFetched === 0
    ? 'Nenhuma publicação encontrada'
    : run.status === 'success' &&
        run.totalInserted === 0 &&
        run.totalSkippedAsDuplicate > 0
      ? 'Sucesso · somente duplicadas'
      : run.status === 'success'
        ? 'Sucesso'
        : run.status === 'partial_error'
          ? 'Sucesso parcial'
          : run.status === 'failed'
            ? 'Falha'
            : run.status}
</td>
                      <td className="py-2 pr-4 text-foreground-700">{run.totalFetched}</td>
                      <td className="py-2 pr-4 text-foreground-700">{run.totalInserted}</td>
                      <td className="py-2 pr-4 text-foreground-700">{run.totalSkippedAsDuplicate}</td>
                      <td className="py-2 pr-4 text-foreground-700">{run.errors.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </details>
  );
}
